/**
 * /security — public security overview. Not a customer-facing
 * marketing page; the audience is Pro/Enterprise prospects whose
 * counsel runs a security questionnaire before signing.
 *
 * Keep technical details concrete enough to satisfy a buyer-side
 * review, but avoid disclosing architecture details that would help an
 * attacker (e.g., specific WAF rules, API rate limits, internal IPs).
 */
import { BrandedLayout } from '@/components/BrandedLayout';

export default function Security() {
  return (
    <BrandedLayout>
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6 prose prose-sm">
        <h1 className="text-2xl font-semibold tracking-tight">
          Security at PharmaSync
        </h1>
        <p className="text-sm text-muted-foreground">
          Last updated: 2026-05-04
        </p>

        <p>
          PharmaSync handles staff scheduling, time-off, and operational
          data for Canadian pharmacies. Below is an overview of how we
          protect that data.
        </p>

        <h2 className="text-lg font-medium">Hosting and data residency</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Database, authentication, file storage</strong> —
            Supabase, primary region <strong>ca-central-1 (Montreal,
            Canada)</strong>.
          </li>
          <li>
            <strong>Web application hosting</strong> — Vercel (global edge
            network, primary compute in Canada/US regions).
          </li>
          <li>
            <strong>DDoS and traffic protection</strong> — Cloudflare.
          </li>
          <li>
            <strong>Email</strong> — Google Workspace.
          </li>
          <li>
            <strong>Payment processing</strong> — Stripe (cross-border to
            the United States; see{' '}
            <a href="/privacy" className="underline">Privacy Policy</a>{' '}
            Section 7.1).
          </li>
        </ul>
        <p>
          A complete sub-processor list is published at{' '}
          <a href="/subprocessors" className="underline">
            /subprocessors
          </a>
          .
        </p>

        <h2 className="text-lg font-medium">Encryption</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>In transit</strong> — TLS 1.2 or higher for all
            connections.
          </li>
          <li>
            <strong>At rest</strong> — AES-256 (or equivalent) at the
            database layer.
          </li>
          <li>
            <strong>Passwords</strong> — never stored in plaintext; hashed
            using industry-standard one-way functions.
          </li>
          <li>
            <strong>Payment cards</strong> — never touch our servers;
            tokenized by Stripe.
          </li>
        </ul>

        <h2 className="text-lg font-medium">Access control</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Role-based access</strong> at the application layer.
            Five tiered roles (Owner, Manager, Pharmacist, Staff, Platform
            Admin), each with scoped visibility.
          </li>
          <li>
            <strong>Row-level security</strong> at the database layer.
            Every query against tenant data is filtered by the database
            itself, not just by application code — a bug in the API
            cannot leak data across tenants.
          </li>
          <li>
            <strong>Multi-factor authentication</strong> is required for
            administrative accounts and is available for all customer
            accounts.
          </li>
          <li>
            <strong>Least privilege</strong> for internal personnel;
            access is logged and reviewed.
          </li>
        </ul>

        <h2 className="text-lg font-medium">Operations</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Vulnerability management</strong> — dependency
            scanning and patching on a regular cadence.
          </li>
          <li>
            <strong>Backups</strong> — automated, encrypted, tested for
            recovery.
          </li>
          <li>
            <strong>Logging and monitoring</strong> of administrative
            access and significant tenant actions (audit logs retained
            for 7 years).
          </li>
          <li>
            <strong>Incident response</strong> — documented internal
            plan; customer notification within 72 hours of confirming a
            Security Incident affecting their data (48 hours for
            Pro/Enterprise tiers, where elected on the Order Form).
          </li>
        </ul>

        <h2 className="text-lg font-medium">Compliance</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>PIPEDA</strong> — Personal Information Protection and
            Electronic Documents Act (Canada).
          </li>
          <li>
            <strong>Quebec Law 25</strong> — privacy regime for processing
            of personal information of Quebec residents, including
            cross-border privacy impact assessment for the Stripe payment
            transfer.
          </li>
          <li>
            <strong>PIA on file</strong> for the Stripe US cross-border
            transfer per Quebec Law 25.
          </li>
        </ul>

        <h2 className="text-lg font-medium">Reporting a vulnerability</h2>
        <p>
          If you believe you have found a security issue, please email{' '}
          <a
            href="mailto:security@pharma-sync.ca"
            className="underline"
          >
            security@pharma-sync.ca
          </a>
          . Include enough detail to reproduce the issue. We acknowledge
          reports within 2 business days and aim to resolve confirmed
          issues quickly. Please do not test with production data of any
          pharmacy other than your own.
        </p>
        <p>
          We currently do not run a public bug bounty program. Responsible
          disclosure is appreciated.
        </p>

        <h2 className="text-lg font-medium">Audit and certifications</h2>
        <p>
          PharmaSync is a pre-launch product. We do not yet hold
          third-party audit certifications (such as SOC 2). We are happy
          to share our security questionnaire responses, internal
          security policies, and Data Processing Addendum on request from
          prospective customers.
        </p>

        <h2 className="text-lg font-medium">Questions</h2>
        <p>
          For security questions:{' '}
          <a
            href="mailto:security@pharma-sync.ca"
            className="underline"
          >
            security@pharma-sync.ca
          </a>
          <br />
          For privacy questions:{' '}
          <a
            href="mailto:privacy@pharma-sync.ca"
            className="underline"
          >
            privacy@pharma-sync.ca
          </a>
        </p>
      </div>
    </div>
    </BrandedLayout>
  );
}
